import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/Navbar";
import IngredientCard from "@/components/IngredientCard";
import BarcodeScanner from "@/components/BarcodeScanner";
import { 
  Link, Search, Camera, FileText, 
  Upload, Loader2, Check, X,
  Sparkles, Package, FlaskConical, ArrowRight, Barcode, Database, Leaf
} from "lucide-react";

export default function AddProduct() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState("name");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState({}); // For multi-select
  
  // Batch analyze state
  const [showBatchAnalyze, setShowBatchAnalyze] = useState(false);
  const [savedProductIds, setSavedProductIds] = useState([]);
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState({ current: 0, total: 0 });
  const [analyzedCount, setAnalyzedCount] = useState(0);
  
  // Form states
  const [productName, setProductName] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [ingredientsText, setIngredientsText] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  
  // Product form for saving
  const [productForm, setProductForm] = useState({
    name: "",
    brand: "",
    category: "",
    ingredients: [],
    image_url: "",
    source_url: "",
    expiration_date: ""
  });

  // Open Beauty Facts search state
  const [obfSearchQuery, setObfSearchQuery] = useState("");
  const [obfSearchResults, setObfSearchResults] = useState([]);
  const [obfSearching, setObfSearching] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);

  // Database search state (search existing products in Supabase)
  const [dbSearchResults, setDbSearchResults] = useState([]);
  const [isDbSearching, setIsDbSearching] = useState(false);

  // Search existing products in database
  const handleDbSearch = async (query) => {
    if (query.length < 3) {
      setDbSearchResults([]);
      return;
    }
    
    setIsDbSearching(true);
    try {
      const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDbSearchResults(response.data.results);
      }
    } catch (err) {
      console.error("Database search failed", err);
      setDbSearchResults([]);
    } finally {
      setIsDbSearching(false);
    }
  };

  // Handle barcode scanned from camera
  const handleBarcodeScan = (scannedBarcode) => {
    setBarcodeInput(scannedBarcode);
    setShowCameraScanner(false);
    toast.success(`Barcode scanned: ${scannedBarcode}`);
    // Auto-lookup the barcode
    lookupBarcodeWithValue(scannedBarcode);
  };

  // Lookup barcode with provided value (for auto-lookup after scan)
  const lookupBarcodeWithValue = async (barcode) => {
    if (!barcode || barcode.length < 8) {
      toast.error("Enter a valid barcode (at least 8 digits)");
      return;
    }
    
    setBarcodeLoading(true);
    try {
      const response = await axios.get(`${API}/obf/barcode/${barcode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const ingredientsList = response.data.ingredients_list?.map(i => i.name || i) || [];
      
      // Set analysis result for display
      setAnalysisResult({
        name: response.data.name,
        brand: response.data.brand,
        category: response.data.category,
        ingredients: ingredientsList,
        source: "Open Beauty Facts",
        barcode: response.data.barcode,
        vegan: response.data.vegan,
        cruelty_free: response.data.cruelty_free
      });
      
      // Also populate the product form for saving
      setProductForm({
        name: response.data.name || "",
        brand: response.data.brand || "",
        category: response.data.category?.toLowerCase() || "",
        ingredients: ingredientsList,
        image_url: response.data.image_url || "",
        source_url: response.data.obf_url || ""
      });
      
      toast.success(`Found: ${response.data.name}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Product not found in Open Beauty Facts database");
      } else {
        toast.error("Failed to lookup barcode");
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  // Search Open Beauty Facts
  const searchOBF = async () => {
    if (!obfSearchQuery || obfSearchQuery.length < 2) {
      toast.error("Enter at least 2 characters to search");
      return;
    }
    
    setObfSearching(true);
    try {
      const response = await axios.get(`${API}/obf/search`, {
        params: { q: obfSearchQuery, limit: 15 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setObfSearchResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info("No products found in Open Beauty Facts database");
      }
    } catch (error) {
      toast.error("Failed to search Open Beauty Facts");
    } finally {
      setObfSearching(false);
    }
  };

  // Lookup barcode (using input field value)
  const lookupBarcode = () => {
    lookupBarcodeWithValue(barcodeInput);
  };

  // Import from OBF search result
  const importFromOBF = async (barcode) => {
    try {
      const response = await axios.post(`${API}/obf/import/${barcode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported: ${response.data.product.name}`);
      navigate("/products");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Similar product already in your collection");
      } else {
        toast.error("Failed to import product");
      }
    }
  };

  const inputMethods = [
    { id: "barcode", icon: Barcode, label: "Scan Barcode", desc: "Enter barcode to find product" },
    { id: "database", icon: Database, label: "Search Database", desc: "Search Open Beauty Facts" },
    { id: "name", icon: Search, label: "Search by Name", desc: "AI search by product name" },
    { id: "url", icon: Link, label: "Paste URL", desc: "Paste product page URL" },
    { id: "ingredients", icon: FileText, label: "Enter Ingredients", desc: "Copy & paste ingredient list" },
    { id: "photo", icon: Camera, label: "Upload Photo", desc: "Photo of product or ingredients" }
  ];

  const categories = [
    "Cleanser", "Toner", "Serum", "Moisturizer", "Sunscreen", 
    "Eye Cream", "Mask", "Exfoliant", "Oil", "Treatment", "Other"
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setImagePreview(base64);
      // Extract just the base64 data without the data URL prefix
      setImageBase64(base64.split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const analyzeProduct = async () => {
    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      let requestData = { input_type: "", data: "" };
      
      switch (activeTab) {
        case "name":
          if (!productName.trim()) {
            toast.error("Please enter a product name");
            setIsAnalyzing(false);
            return;
          }
          requestData = { input_type: "name", data: productName };
          break;
        case "url":
          if (!productUrl.trim()) {
            toast.error("Please enter a product URL");
            setIsAnalyzing(false);
            return;
          }
          requestData = { input_type: "url", data: productUrl };
          break;
        case "ingredients":
          if (!ingredientsText.trim()) {
            toast.error("Please enter ingredients");
            setIsAnalyzing(false);
            return;
          }
          requestData = { input_type: "ingredients", data: ingredientsText };
          break;
        case "photo":
          if (!imageBase64) {
            toast.error("Please upload an image");
            setIsAnalyzing(false);
            return;
          }
          requestData = { input_type: "image_base64", data: imageBase64 };
          break;
        default:
          break;
      }
      
      const response = await axios.post(`${API}/analyze/product`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000 // 5 minutes for multiple products
      });
      
      if (response.data.success) {
        setAnalysisResult(response.data.data);
        const data = response.data.data;
        
        // Check for multiple products
        if (data.multiple_products_detected && data.analyzed_products && data.analyzed_products.length > 1) {
          // Auto-select all products with ingredients
          const initialSelection = {};
          data.analyzed_products.forEach((p, idx) => {
            initialSelection[idx] = p.ingredients && p.ingredients.length > 0;
          });
          setSelectedProducts(initialSelection);
          
          const withIngredients = data.analyzed_products.filter(p => p.ingredients && p.ingredients.length > 0).length;
          toast.success(`Found ${data.analyzed_products.length} products! ${withIngredients} ready to add.`);
        } else {
          // Single product - pre-fill form
          setProductForm({
            name: data.name || data.product_name || productName || "",
            brand: data.brand || "",
            category: data.category || data.product_category || "",
            ingredients: data.ingredients || data.ingredients_list || [],
            image_url: imagePreview || "",
            source_url: data.source_url || productUrl || ""
          });
          
          if (data.analyzed_ingredients && data.analyzed_ingredients.length > 0) {
            toast.success(`Found ${data.analyzed_ingredients.length} ingredients!`);
          } else if (data.likely_products && data.likely_products.length > 0) {
            toast.success("Identified possible product match from ingredients!");
          } else if (data.note) {
            toast.info(data.note);
          } else {
            toast.success("Analysis complete!");
          }
        }
      } else {
        toast.error(response.data.error || "Analysis failed");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze product. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveProduct = async () => {
    if (!productForm.name.trim()) {
      toast.error("Please enter a product name");
      return;
    }
    
    setIsSaving(true);
    try {
      const productData = {
        name: productForm.name,
        brand: productForm.brand || null,
        category: productForm.category || null,
        ingredients: productForm.ingredients.map(i => typeof i === 'string' ? i : i.name),
        image_url: productForm.image_url || null,
        source_url: productForm.source_url || productUrl || null,
        expiration_date: productForm.expiration_date || null
      };
      
      await axios.post(`${API}/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Product saved successfully!");
      navigate("/products");
    } catch (error) {
      console.error("Save product error:", error);
      toast.error(error.response?.data?.detail || "Failed to save product. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const resetAnalysis = () => {
    setAnalysisResult(null);
    setProductName("");
    setProductUrl("");
    setIngredientsText("");
    setImagePreview(null);
    setImageBase64(null);
    setSelectedProductIndex(null);
    setSelectedProducts({});
    setProductForm({ name: "", brand: "", category: "", ingredients: [], image_url: "", source_url: "", expiration_date: "" });
  };

  const toggleProductSelection = (index) => {
    setSelectedProducts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const selectAllProducts = () => {
    const allSelected = {};
    analysisResult.analyzed_products.forEach((p, idx) => {
      allSelected[idx] = true;
    });
    setSelectedProducts(allSelected);
  };

  const deselectAllProducts = () => {
    setSelectedProducts({});
  };

  const saveSelectedProducts = async () => {
    const productsToSave = analysisResult.analyzed_products
      .filter((_, idx) => selectedProducts[idx])
      .map(p => ({
        product_name: p.product_name || p.name,
        brand: p.brand,
        category: p.category,
        ingredients: p.ingredients || [],
        analyzed_ingredients: p.analyzed_ingredients || [],
        image_url: imagePreview || ""
      }));
    
    if (productsToSave.length === 0) {
      toast.error("Please select at least one product to save");
      return;
    }
    
    setIsSaving(true);
    try {
      console.log("Saving products:", productsToSave);
      const response = await axios.post(`${API}/products/bulk`, 
        { products: productsToSave },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // 1 minute timeout for save
        }
      );
      
      console.log("Save response:", response.data);
      const savedIds = response.data.saved.map(p => p.id);
      setSavedProductIds(savedIds);
      
      if (savedIds.length > 0) {
        toast.success(`Saved ${savedIds.length} products!`);
        setShowBatchAnalyze(true);
        setAnalyzeProgress({ current: 0, total: savedIds.length });
      } else {
        toast.info("No new products were saved (they may already exist)");
        navigate("/products");
      }
    } catch (error) {
      console.error("Bulk save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save products. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const batchAnalyzeProducts = async () => {
    if (savedProductIds.length === 0) return;
    
    setBatchAnalyzing(true);
    setAnalyzedCount(0);
    
    for (let i = 0; i < savedProductIds.length; i++) {
      setAnalyzeProgress({ current: i + 1, total: savedProductIds.length });
      
      try {
        await axios.post(`${API}/products/${savedProductIds[i]}/analyze`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        });
        setAnalyzedCount(prev => prev + 1);
      } catch (error) {
        console.error(`Failed to analyze product ${savedProductIds[i]}:`, error);
        // Continue with next product even if one fails
      }
    }
    
    setBatchAnalyzing(false);
    toast.success(`Analyzed ${analyzedCount + 1} products! Ratings are now available.`);
    navigate("/products");
  };

  const skipAnalysis = () => {
    toast.info("You can analyze products later by clicking on them");
    navigate("/products");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="add-product-page">
      <Navbar />
      
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Batch Analyze Screen */}
        {showBatchAnalyze ? (
          <div className="animate-fade-in-up">
            <Card className="card-product rounded-3xl max-w-lg mx-auto">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  {batchAnalyzing ? (
                    <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
                  ) : (
                    <Check className="w-8 h-8 text-green-600" />
                  )}
                </div>
                <CardTitle className="font-serif text-2xl">
                  {batchAnalyzing 
                    ? `Analyzing Products...` 
                    : `${savedProductIds.length} Products Saved!`
                  }
                </CardTitle>
                <CardDescription>
                  {batchAnalyzing 
                    ? "Getting ingredient ratings, benefits, and concerns"
                    : "Would you like to analyze ingredients now?"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {batchAnalyzing ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Progress</span>
                        <span className="font-medium">
                          {analyzeProgress.current} of {analyzeProgress.total}
                        </span>
                      </div>
                      <Progress 
                        value={(analyzeProgress.current / analyzeProgress.total) * 100} 
                        className="h-3"
                      />
                    </div>
                    <p className="text-sm text-center text-muted-foreground">
                      This may take {analyzeProgress.total * 5}-{analyzeProgress.total * 10} seconds
                    </p>
                  </>
                ) : (
                  <>
                    <div className="bg-primary/5 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <FlaskConical className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">What analysis provides:</p>
                          <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                            <li>• Safety ratings for each ingredient (1-5 stars)</li>
                            <li>• Benefits and potential concerns</li>
                            <li>• Tips for your skincare routine</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-3">
                      <Button 
                        onClick={batchAnalyzeProducts}
                        className="btn-primary w-full"
                        data-testid="analyze-all-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analyze All {savedProductIds.length} Products
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={skipAnalysis}
                        className="w-full"
                        data-testid="skip-analysis-btn"
                      >
                        Skip for Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      You can also analyze products later by clicking on them
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            <div className="mb-8 animate-fade-in-up">
              <h1 className="font-serif text-3xl text-primary mb-2">Add Product</h1>
              <p className="text-muted-foreground">
                Choose how you'd like to add your skincare product
              </p>
            </div>

            {!analysisResult ? (
          <div className="space-y-6 animate-fade-in-up stagger-1">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 h-auto p-2 bg-secondary/50">
                {inputMethods.map(method => (
                  <TabsTrigger 
                    key={method.id}
                    value={method.id}
                    className="flex flex-col items-center gap-2 p-4 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                    data-testid={`tab-${method.id}`}
                  >
                    <method.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{method.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Barcode Lookup */}
              <TabsContent value="barcode" className="mt-6">
                <Card className="card-product rounded-3xl">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <Barcode className="w-5 h-5" />
                      Scan Barcode
                    </CardTitle>
                    <CardDescription>
                      Use your camera to scan the barcode or enter the number manually
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Camera Scanner */}
                    {showCameraScanner ? (
                      <BarcodeScanner 
                        onScan={handleBarcodeScan}
                        onClose={() => setShowCameraScanner(false)}
                      />
                    ) : (
                      <>
                        {/* Camera Button */}
                        <Button
                          variant="outline"
                          className="w-full h-24 border-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 transition-all"
                          onClick={() => setShowCameraScanner(true)}
                          data-testid="open-camera-btn"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <Camera className="w-8 h-8 text-primary" />
                            <span className="font-medium text-primary">Use Camera to Scan</span>
                          </div>
                        </Button>

                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">or enter manually</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="barcode">Barcode Number</Label>
                          <div className="flex gap-3">
                            <Input
                              id="barcode"
                              placeholder="e.g., 3700225618512"
                              value={barcodeInput}
                              onChange={(e) => setBarcodeInput(e.target.value.replace(/\D/g, ''))}
                              className="input-skiniq flex-1"
                              data-testid="barcode-input"
                              onKeyDown={(e) => e.key === 'Enter' && lookupBarcode()}
                            />
                            <Button 
                              onClick={lookupBarcode}
                              disabled={barcodeLoading || barcodeInput.length < 8}
                              className="btn-primary"
                              data-testid="barcode-lookup-btn"
                            >
                              {barcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Usually found on the product packaging as a series of numbers
                          </p>
                        </div>
                      </>
                    )}
                    
                    <div className="bg-primary/5 rounded-xl p-4 flex items-start gap-3">
                      <Database className="w-5 h-5 text-primary mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium">Powered by Open Beauty Facts</p>
                        <p className="text-muted-foreground">100,000+ cosmetic products with ingredient data</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Database Search */}
              <TabsContent value="database" className="mt-6">
                <Card className="card-product rounded-3xl">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <Database className="w-5 h-5" />
                      Search Product Database
                    </CardTitle>
                    <CardDescription>
                      Search the Open Beauty Facts database for your product
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="obf-search">Search Products</Label>
                      <div className="flex gap-3">
                        <Input
                          id="obf-search"
                          placeholder="e.g., CeraVe, The Ordinary, La Roche-Posay..."
                          value={obfSearchQuery}
                          onChange={(e) => setObfSearchQuery(e.target.value)}
                          className="input-skiniq flex-1"
                          data-testid="obf-search-input"
                          onKeyDown={(e) => e.key === 'Enter' && searchOBF()}
                        />
                        <Button 
                          onClick={searchOBF}
                          disabled={obfSearching || obfSearchQuery.length < 2}
                          className="btn-primary"
                          data-testid="obf-search-btn"
                        >
                          {obfSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Search Results */}
                    {obfSearchResults.length > 0 && (
                      <ScrollArea className="h-[400px] rounded-xl border">
                        <div className="p-3 space-y-2">
                          {obfSearchResults.map((product, idx) => (
                            <Card 
                              key={product.barcode || idx}
                              className="cursor-pointer hover:bg-primary/5 transition-colors"
                              onClick={() => importFromOBF(product.barcode)}
                              data-testid={`obf-result-${idx}`}
                            >
                              <CardContent className="p-3 flex items-center gap-3">
                                {product.image_url ? (
                                  <img src={product.image_url} alt="" className="w-12 h-12 object-cover rounded-lg" />
                                ) : (
                                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Package className="w-6 h-6 text-primary/50" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{product.name}</p>
                                  {product.brand && (
                                    <p className="text-xs text-muted-foreground">{product.brand}</p>
                                  )}
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="text-[10px]">{product.category}</Badge>
                                    {product.has_ingredients && (
                                      <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700">
                                        <Check className="w-3 h-3 mr-0.5" />
                                        Has Ingredients
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                    
                    <div className="bg-primary/5 rounded-xl p-4 flex items-start gap-3">
                      <Leaf className="w-5 h-5 text-green-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium">Product data includes:</p>
                        <p className="text-muted-foreground">Ingredients, vegan status, cruelty-free labels & more</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Search by Name */}
              <TabsContent value="name" className="mt-6">
                <Card className="card-product rounded-3xl">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl">Search by Product Name</CardTitle>
                    <CardDescription>
                      Enter the product name and our AI will find the ingredients
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="product-name">Product Name</Label>
                      <div className="flex gap-3">
                        <Input
                          id="product-name"
                          placeholder="e.g., CeraVe Hydrating Cleanser"
                          value={productName}
                          onChange={(e) => {
                            setProductName(e.target.value);
                            handleDbSearch(e.target.value);
                          }}
                          className="input-skiniq flex-1"
                          data-testid="product-name-input"
                        />
                      </div>
                    </div>
                    
                    {/* Database Search Results */}
                    {isDbSearching && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Searching database...
                      </div>
                    )}
                    
                    {dbSearchResults.length > 0 && !isDbSearching && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-primary">Found in database:</p>
                        <ScrollArea className="h-[280px]">
                          <div className="space-y-2 pr-4">
                            {dbSearchResults.map((product, idx) => (
                              <div 
                                key={product.product_name || idx}
                                className="p-4 border-2 rounded-xl flex justify-between items-start cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                                onClick={() => {
                                  setProductForm({
                                    name: product.product_name || "",
                                    brand: product.brand_name || "",
                                    category: product.category?.toLowerCase() || "",
                                    ingredients: [],
                                    image_url: product.image_url || "",
                                    source_url: ""
                                  });
                                  setProductName(product.product_name);
                                  setDbSearchResults([]);
                                  toast.success(`Selected: ${product.product_name}`);
                                }}
                                data-testid={`db-result-${idx}`}
                              >
                                <div className="flex-1">
                                  <h3 className="font-semibold text-primary">{product.product_name}</h3>
                                  {product.brand_name && (
                                    <p className="text-sm text-muted-foreground">{product.brand_name}</p>
                                  )}
                                  
                                  {product.is_banned_in_eu && (
                                    <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full mt-2 inline-block">
                                      ⚠️ EU Banned Ingredients Found
                                    </span>
                                  )}
                                </div>

                                {product.skiniq_score !== undefined && (
                                  <div className={`text-2xl font-bold ${
                                    product.skiniq_score >= 80 ? 'text-green-500' : 
                                    product.skiniq_score >= 50 ? 'text-yellow-500' : 'text-red-500'
                                  }`}>
                                    {product.skiniq_score}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Paste URL */}
              <TabsContent value="url" className="mt-6">
                <Card className="card-product rounded-3xl">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl">Paste Product URL</CardTitle>
                    <CardDescription>
                      Paste a link to the product page from any retailer
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="product-url">Product URL</Label>
                      <Input
                        id="product-url"
                        type="url"
                        placeholder="https://..."
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                        className="input-skiniq"
                        data-testid="product-url-input"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Enter Ingredients */}
              <TabsContent value="ingredients" className="mt-6">
                <Card className="card-product rounded-3xl">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl">Enter Ingredients</CardTitle>
                    <CardDescription>
                      Copy and paste the ingredients list from the product
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ingredients">Ingredients List</Label>
                      <Textarea
                        id="ingredients"
                        placeholder="Water, Glycerin, Niacinamide, Hyaluronic Acid..."
                        value={ingredientsText}
                        onChange={(e) => setIngredientsText(e.target.value)}
                        className="min-h-32 input-skiniq"
                        data-testid="ingredients-textarea"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-name">Product Name (optional)</Label>
                      <Input
                        id="manual-name"
                        placeholder="Product name for reference"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        className="input-skiniq"
                        data-testid="manual-name-input"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Upload Photo */}
              <TabsContent value="photo" className="mt-6">
                <Card className="card-product rounded-3xl">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl">Upload Photo</CardTitle>
                    <CardDescription>
                      Upload a photo of the product or ingredients label
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                    
                    {!imagePreview ? (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-border rounded-2xl p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                        data-testid="upload-dropzone"
                      >
                        <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="font-medium text-primary">Click to upload</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          JPEG, PNG, or WebP (max 10MB)
                        </p>
                      </div>
                    ) : (
                      <div className="relative rounded-2xl overflow-hidden">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-64 object-cover"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-3 right-3 rounded-full"
                          onClick={() => {
                            setImagePreview(null);
                            setImageBase64(null);
                          }}
                          data-testid="remove-image-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Analyze Button */}
            <div className="flex flex-col items-end gap-2">
              {isAnalyzing && (
                <p className="text-sm text-amber-600 font-medium">
                  ⚠️ Please stay on this page while analyzing. Navigating away will cancel the request.
                </p>
              )}
              <Button 
                onClick={analyzeProduct}
                disabled={isAnalyzing}
                className="btn-primary"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing (this may take a minute for multiple products)...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze Product
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Analysis Results */
          <div className="space-y-6 animate-fade-in-up">
            <Card className="card-product rounded-3xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-serif text-2xl">Analysis Results</CardTitle>
                    <CardDescription>Review and save your product</CardDescription>
                  </div>
                  <Button variant="ghost" onClick={resetAnalysis} data-testid="reset-analysis-btn">
                    <X className="w-4 h-4 mr-2" />
                    Start Over
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Multiple Products Detected */}
                {analysisResult.multiple_products_detected && analysisResult.analyzed_products && analysisResult.analyzed_products.length > 1 && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Package className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-800">
                            {analysisResult.analyzed_products.length} Products Detected
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={selectAllProducts} className="text-xs">
                            Select All
                          </Button>
                          <Button variant="ghost" size="sm" onClick={deselectAllProducts} className="text-xs">
                            Deselect All
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-blue-700 mb-4">
                        All products analyzed automatically. Select which ones to add to your library:
                      </p>
                      <div className="grid gap-3">
                        {analysisResult.analyzed_products.map((product, idx) => (
                          <div
                            key={idx}
                            onClick={() => toggleProductSelection(idx)}
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                              selectedProducts[idx] 
                                ? "border-primary bg-primary/5" 
                                : "border-border bg-white hover:border-primary/30"
                            }`}
                            data-testid={`product-checkbox-${idx}`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedProducts[idx] ? "border-primary bg-primary" : "border-gray-300"
                            }`}>
                              {selectedProducts[idx] && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-primary truncate">
                                {product.product_name || product.name || `Product ${idx + 1}`}
                              </p>
                              {product.brand && (
                                <p className="text-sm text-muted-foreground">{product.brand}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {product.category && (
                                  <Badge variant="outline" className="text-xs capitalize">{product.category}</Badge>
                                )}
                                <Badge 
                                  variant={product.ingredients?.length > 0 ? "default" : "secondary"} 
                                  className={`text-xs ${product.ingredients?.length > 0 ? "bg-green-600" : ""}`}
                                >
                                  {product.ingredients?.length || 0} ingredients
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {product.source === "ocr_direct" ? "From image" : product.source === "online_search" ? "Found online" : "Pending"}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Bulk Save Button */}
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <Button 
                          onClick={saveSelectedProducts}
                          disabled={isSaving || Object.values(selectedProducts).filter(Boolean).length === 0}
                          className="w-full btn-primary"
                          data-testid="save-selected-products-btn"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Add {Object.values(selectedProducts).filter(Boolean).length} Product(s) to Library
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Single Product Results - Source/Method Info */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) && (analysisResult.source || analysisResult.ocr_method) && (
                  <div className="p-3 bg-primary/5 rounded-xl text-sm">
                    <span className="font-medium text-primary">Source: </span>
                    <span className="text-muted-foreground">
                      {analysisResult.ocr_method === "google_cloud_vision" && "Google Cloud Vision OCR"}
                      {analysisResult.ocr_method === "gpt_vision_fallback" && "AI Vision Analysis"}
                      {analysisResult.source === "ocr_direct" && " - Ingredients extracted directly from image"}
                      {analysisResult.source === "ocr_plus_online_search" && " - Product identified, ingredients found online"}
                      {analysisResult.source === "online_search" && "Product information found online"}
                      {analysisResult.source === "Open Beauty Facts" && "Open Beauty Facts Database"}
                    </span>
                  </div>
                )}

                {/* Detected Brand Logos - Single product only */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) && analysisResult.detected_logos && analysisResult.detected_logos.length > 0 && (
                  <div className="p-3 bg-green-50 rounded-xl text-sm">
                    <span className="font-medium text-green-800">Detected Brand: </span>
                    <span className="text-green-700">{analysisResult.detected_logos.join(", ")}</span>
                  </div>
                )}

                {/* Likely Products (from reverse ingredient search) - Single product only */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) && analysisResult.likely_products && analysisResult.likely_products.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-xl space-y-2">
                    <Label className="text-amber-800">Possible Product Match</Label>
                    {analysisResult.likely_products.slice(0, 2).map((product, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium">{product.brand} {product.name}</span>
                        <span className="text-muted-foreground ml-2">({product.confidence} confidence)</span>
                        {product.reason && <p className="text-xs text-muted-foreground mt-1">{product.reason}</p>}
                      </div>
                    ))}
                    <p className="text-xs text-amber-700 mt-2">
                      Click a suggestion to use it, or enter details manually below.
                    </p>
                  </div>
                )}

                {/* Notes/Warnings - Single product only */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) && analysisResult.note && (
                  <div className="p-3 bg-secondary/50 rounded-xl text-sm text-muted-foreground">
                    {analysisResult.note}
                  </div>
                )}

                {/* Manual ingredient paste fallback when URL extraction returns 0 ingredients */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) &&
                 (!analysisResult.analyzed_ingredients || analysisResult.analyzed_ingredients.length === 0) &&
                 (!analysisResult.ingredients || analysisResult.ingredients.length === 0) && (
                  <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl space-y-3" data-testid="manual-ingredients-fallback">
                    <div>
                      <p className="font-semibold text-amber-900 text-sm">No ingredients found on this page</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Some retailers (Estée Lauder, Sephora, Lancôme) block automated lookups. Paste the ingredient list here for accurate analysis:
                      </p>
                    </div>
                    <Textarea
                      placeholder="Aqua, Glycerin, Niacinamide, Sodium Hyaluronate, ..."
                      value={ingredientsText}
                      onChange={(e) => setIngredientsText(e.target.value)}
                      rows={4}
                      className="bg-white"
                      data-testid="manual-ingredients-textarea"
                    />
                    <Button
                      onClick={async () => {
                        if (!ingredientsText.trim()) {
                          toast.error("Please paste ingredients first");
                          return;
                        }
                        setIsAnalyzing(true);
                        try {
                          const res = await axios.post(`${API}/analyze/product`,
                            { input_type: "ingredients", data: ingredientsText },
                            { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
                          );
                          if (res.data.success) {
                            const merged = {
                              ...analysisResult,
                              ingredients: res.data.data.ingredients || [],
                              analyzed_ingredients: res.data.data.analyzed_ingredients || [],
                            };
                            setAnalysisResult(merged);
                            setProductForm(prev => ({
                              ...prev,
                              ingredients: merged.ingredients,
                            }));
                            toast.success(`Analyzed ${(merged.analyzed_ingredients || []).length} ingredients!`);
                          }
                        } catch (e) {
                          toast.error("Analysis failed. Please try again.");
                        } finally {
                          setIsAnalyzing(false);
                        }
                      }}
                      disabled={isAnalyzing || !ingredientsText.trim()}
                      className="btn-primary"
                      data-testid="analyze-pasted-ingredients-btn"
                    >
                      {isAnalyzing ? "Analyzing..." : "Analyze Pasted Ingredients"}
                    </Button>
                  </div>
                )}

                {/* Suggestions - Single product only */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) && analysisResult.suggestions && analysisResult.suggestions.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-xl text-sm">
                    <p className="font-medium text-blue-800 mb-2">Tips for better results:</p>
                    <ul className="list-disc list-inside text-blue-700 space-y-1">
                      {analysisResult.suggestions.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Product Info Form - Only for single product */}
                {(!analysisResult.multiple_products_detected || analysisResult.analyzed_products?.length <= 1) && (
                <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="save-name">Product Name *</Label>
                    <Input
                      id="save-name"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      className="input-skiniq"
                      data-testid="save-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="save-brand">Brand</Label>
                    <Input
                      id="save-brand"
                      value={productForm.brand}
                      onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                      className="input-skiniq"
                      data-testid="save-brand-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Category</Label>
                    <Select 
                      value={productForm.category} 
                      onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                    >
                      <SelectTrigger className="input-skiniq" data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat.toLowerCase()}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="save-expiration">Expiration Date (optional)</Label>
                    <Input
                      id="save-expiration"
                      type="date"
                      value={productForm.expiration_date}
                      onChange={(e) => setProductForm({ ...productForm, expiration_date: e.target.value })}
                      className="input-skiniq"
                      data-testid="save-expiration-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Track shelf life — we'll warn you before it expires.
                    </p>
                  </div>
                </div>

                {/* Analyzed Ingredients */}
                {analysisResult.analyzed_ingredients && analysisResult.analyzed_ingredients.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base">Analyzed Ingredients</Label>
                      <Badge variant="outline" className="text-xs">
                        {analysisResult.analyzed_ingredients.length} found
                      </Badge>
                    </div>
                    <ScrollArea className="h-80 rounded-xl border border-border p-4">
                      <div className="space-y-3">
                        {analysisResult.analyzed_ingredients.map((ingredient, idx) => (
                          <IngredientCard key={idx} ingredient={ingredient} />
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Raw Ingredients List */}
                {(!analysisResult.analyzed_ingredients || analysisResult.analyzed_ingredients.length === 0) && 
                  analysisResult.ingredients && (
                  <div className="space-y-3">
                    <Label className="text-base">Ingredients Found</Label>
                    <div className="flex flex-wrap gap-2 p-4 bg-secondary/30 rounded-xl">
                      {(Array.isArray(analysisResult.ingredients) 
                        ? analysisResult.ingredients 
                        : analysisResult.ingredients.split(",")
                      ).map((ing, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {typeof ing === 'string' ? ing.trim() : ing.name || ing}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-border">
                  <Button 
                    onClick={saveProduct}
                    disabled={isSaving}
                    className="btn-primary"
                    data-testid="save-product-btn"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save Product
                      </>
                    )}
                  </Button>
                </div>
                </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}
